// Custom hook that subscribes to the DraftChannel via Action Cable and wires
// the sync core (state_version tracking, gap detection, refetch — see
// draftSync.ts) to React state.

import { useEffect, useRef, useState } from 'react'
import type { Subscription } from '@rails/actioncable'
import { getConsumer } from '../lib/cable'
import { api } from '../lib/api'
import { recordServerTime } from '../lib/clock'
import { createDraftSync } from './draftSync'
import type { DraftSync } from './draftSync'
import type { DraftState, DraftResponse } from '../types/protocol'

interface UseDraftChannelOptions {
  code: string
  teamToken: string | null
  onRankingsImported?: () => void
}

interface UseDraftChannelReturn {
  draft: DraftState | null
  loading: boolean
  error: string | null
}

export function useDraftChannel({
  code,
  teamToken,
  onRankingsImported,
}: UseDraftChannelOptions): UseDraftChannelReturn {
  const [draft, setDraft] = useState<DraftState | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const subscriptionRef = useRef<Subscription | null>(null)
  const onRankingsImportedRef = useRef(onRankingsImported)
  onRankingsImportedRef.current = onRankingsImported

  const joinCode = code.toUpperCase()

  // Request identity is read through refs so the sync core outlives a team
  // token arriving late (claim) with its tracked state_version intact.
  const joinCodeRef = useRef(joinCode)
  joinCodeRef.current = joinCode
  const teamTokenRef = useRef(teamToken)
  teamTokenRef.current = teamToken

  const syncRef = useRef<DraftSync | null>(null)
  if (!syncRef.current) {
    syncRef.current = createDraftSync({
      fetchState: async () => {
        const response = await api<DraftResponse>(`/drafts/${joinCodeRef.current}`, {
          teamToken: teamTokenRef.current ?? undefined,
        })
        // The cable feeds recordServerTime for every event, but a page that
        // renders a countdown before its first broadcast would otherwise run at
        // offset 0 for its whole life — a phone with a wrong clock showing a
        // wrong countdown to a room watching a right one. The refetch is the
        // only other moment the server tells us what time it is, and it fires
        // on the initial load, on reconnect, on every gap-triggered refetch and
        // on visibilitychange, which is exactly the set of moments a long-lived
        // tab's offset should be refreshed.
        recordServerTime(response.server_now_ms)
        return response.draft
      },
      onChange: (snapshot) => {
        setDraft(snapshot.draft)
        setLoading(snapshot.loading)
        setError(snapshot.error)
      },
      onRankingsImported: () => onRankingsImportedRef.current?.(),
      onServerTime: recordServerTime,
    })
  }
  const sync = syncRef.current

  // Subscribe to the cable channel
  useEffect(() => {
    // Initial fetch
    void sync.refetch()

    const consumer = getConsumer()
    const subscription = consumer.subscriptions.create(
      { channel: 'DraftChannel', code: joinCode },
      {
        connected() {
          // Refetch on connect/reconnect to catch events missed while
          // disconnected.
          void sync.refetch()
        },
        disconnected() {
          // Nothing needed — reconnect triggers connected() again.
        },
        rejected() {
          setError('Could not join the draft channel')
          setLoading(false)
        },
        received(data: unknown) {
          sync.handleEvent(data)
        },
      },
    )

    subscriptionRef.current = subscription

    return () => {
      subscription.unsubscribe()
      subscriptionRef.current = null
    }
  }, [joinCode, teamToken, sync])

  // Refetch on visibilitychange -> visible (for throttled mobile browsers).
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        void sync.refetch()
      }
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [sync])

  return { draft, loading, error }
}
